/**
 * Sample Chatbot Flows
 * Trigger keyword user change kar sakta hai.
 * System variables: {{contact_name}}, {{wa_id}}, {{timestamp}}, {{last_user_message}}
 */

export const SAMPLE_FLOWS = [
  {
    name: 'Welcome & Main Menu',
    trigger_keyword: 'hi',
    description: 'Greet user and show a main menu. Best starting point for any support bot.',
    icon: '👋',
    flow_json: {
      steps: [
        { id: 'start', type: 'message', text: 'Hello {{contact_name}}! 👋\n\nWelcome! How can I help you today?', next: 'main_menu' },
        {
          id: 'main_menu', type: 'question',
          text: 'Please choose:\n\n1️⃣ Product Info\n2️⃣ Order Status\n3️⃣ Talk to Agent\n4️⃣ Working Hours',
          variable: 'menu_choice', fallback: 'Please reply 1, 2, 3 or 4.',
          options: [
            { match: '1', reply: null, next: 'products' },
            { match: '2', reply: null, next: 'order' },
            { match: '3', reply: null, next: 'escalate' },
            { match: '4', reply: 'We are open Mon-Sat, 10am to 7pm.', next: 'ask_more' },
            { match: '*', reply: null, next: 'main_menu' },
          ],
        },
        { id: 'products', type: 'message', text: 'Our Plans:\n\nBasic - 999/month\nPro - 2499/month\nEnterprise - Custom\n\nReply with your choice or ask any question!', next: 'ask_more' },
        { id: 'order', type: 'question', text: 'Please share your Order ID:', variable: 'order_id', input_type: 'text', next: 'order_reply' },
        { id: 'order_reply', type: 'message', text: 'Checking order #{{order_id}}... Our team will update you within 2 hours!', next: 'ask_more' },
        {
          id: 'ask_more', type: 'question', text: 'Anything else I can help? (yes / no)',
          variable: 'wants_more', fallback: 'Please reply yes or no.',
          options: [
            { match: 'yes', reply: null, next: 'main_menu' },
            { match: 'no', reply: null, next: 'end' },
            { match: '*', reply: null, next: 'main_menu' },
          ],
        },
        { id: 'escalate', type: 'escalate', message: 'Connecting you to our support team. Please wait...' },
        { id: 'end', type: 'end', text: 'Thank you {{contact_name}}! Have a great day! ' },
      ],
    },
  },
  {
    name: 'Lead Qualification',
    trigger_keyword: 'interested',
    description: 'Collect lead details — name, email, budget, requirements. Great for sales teams.',
    icon: '🎯',
    flow_json: {
      steps: [
        { id: 'start', type: 'message', text: 'Hi {{contact_name}}!  Great to hear you are interested.\n\nLet me ask a few quick questions.', next: 'ask_name' },
        { id: 'ask_name', type: 'question', text: 'What is your full name?', variable: 'full_name', input_type: 'text', next: 'ask_email' },
        { id: 'ask_email', type: 'question', text: 'What is your email address?', variable: 'email', input_type: 'email', validation_error: 'Please enter a valid email (e.g. name@example.com)', next: 'ask_budget' },
        {
          id: 'ask_budget', type: 'question',
          text: 'What is your monthly budget?\n\n1 Under 5000\n2 5000-20000\n3 20000-50000\n4 Above 50000',
          variable: 'budget', fallback: 'Please reply 1, 2, 3 or 4.',
          options: [
            { match: '1', reply: null, next: 'ask_requirement' },
            { match: '2', reply: null, next: 'ask_requirement' },
            { match: '3', reply: null, next: 'ask_requirement' },
            { match: '4', reply: null, next: 'ask_requirement' },
            { match: '*', reply: null, next: 'ask_budget' },
          ],
        },
        { id: 'ask_requirement', type: 'question', text: 'Briefly describe what you need:', variable: 'requirement', input_type: 'text', next: 'confirm' },
        { id: 'confirm', type: 'message', text: 'Got it!\n\nName: {{full_name}}\nEmail: {{email}}\nBudget: Option {{budget}}\nNeed: {{requirement}}\n\nOur team will contact you within 24 hours!', next: 'end' },
        { id: 'end', type: 'end', text: 'Thank you {{full_name}}! We will reach out to {{email}} soon. ' },
      ],
    },
  },
  {
    name: 'Appointment Booking',
    trigger_keyword: 'appointment',
    description: 'Book an appointment — collect date, time, service type. Good for clinics, salons, consultants.',
    icon: '📅',
    flow_json: {
      steps: [
        { id: 'start', type: 'message', text: 'Hi {{contact_name}}!  Let us book your appointment.\n\nJust a few quick questions!', next: 'ask_service' },
        {
          id: 'ask_service', type: 'question',
          text: 'Which service do you need?\n\n1 General Consultation\n2 Follow-up Visit\n3 Specialist\n4 Emergency',
          variable: 'service_type', fallback: 'Please reply 1, 2, 3 or 4.',
          options: [
            { match: '1', reply: 'General Consultation selected', next: 'ask_date' },
            { match: '2', reply: 'Follow-up Visit selected', next: 'ask_date' },
            { match: '3', reply: 'Specialist selected', next: 'ask_date' },
            { match: '4', reply: null, next: 'emergency' },
            { match: '*', reply: null, next: 'ask_service' },
          ],
        },
        { id: 'ask_date', type: 'question', text: 'What date works for you? (e.g. 25 June 2025)', variable: 'preferred_date', input_type: 'text', next: 'ask_time' },
        {
          id: 'ask_time', type: 'question',
          text: 'Preferred time slot?\n\n1 Morning (9am-12pm)\n2 Afternoon (12pm-3pm)\n3 Evening (3pm-6pm)',
          variable: 'preferred_time', fallback: 'Please reply 1, 2 or 3.',
          options: [
            { match: '1', reply: 'Morning slot noted', next: 'confirm' },
            { match: '2', reply: 'Afternoon slot noted', next: 'confirm' },
            { match: '3', reply: 'Evening slot noted', next: 'confirm' },
            { match: '*', reply: null, next: 'ask_time' },
          ],
        },
        { id: 'confirm', type: 'message', text: 'Appointment Request Received!\n\nService: Option {{service_type}}\nDate: {{preferred_date}}\nTime: Slot {{preferred_time}}\nPhone: {{wa_id}}\n\nWe will confirm within 2 hours!', next: 'end' },
        { id: 'emergency', type: 'escalate', message: 'Emergency request received! Connecting you immediately...' },
        { id: 'end', type: 'end', text: 'Appointment request submitted, {{contact_name}}! See you soon!' },
      ],
    },
  },
  {
    name: 'FAQ Bot',
    trigger_keyword: 'help',
    description: 'Answer frequently asked questions in a loop. User can ask multiple questions.',
    icon: '❓',
    flow_json: {
      steps: [
        { id: 'start', type: 'message', text: 'Hi {{contact_name}}! I am your FAQ assistant.\n\nI can answer common questions instantly!', next: 'faq_menu' },
        {
          id: 'faq_menu', type: 'question',
          text: 'What would you like to know?\n\n1 Pricing and Plans\n2 Delivery and Shipping\n3 Returns and Refunds\n4 Payment Methods\n5 Contact Support',
          variable: 'faq_choice', fallback: 'Please choose 1 to 5.',
          options: [
            { match: '1', reply: null, next: 'ans_pricing' },
            { match: '2', reply: null, next: 'ans_delivery' },
            { match: '3', reply: null, next: 'ans_returns' },
            { match: '4', reply: null, next: 'ans_payment' },
            { match: '5', reply: null, next: 'escalate' },
            { match: '*', reply: null, next: 'faq_menu' },
          ],
        },
        { id: 'ans_pricing', type: 'message', text: 'Pricing and Plans\n\nBasic: 999/month - 1 user, 500 msgs/day\nPro: 2499/month - 5 users, 5000 msgs/day\nBusiness: 7999/month - Unlimited\n\nAll plans include 14-day free trial!', next: 'ask_more' },
        { id: 'ans_delivery', type: 'message', text: 'Delivery and Shipping\n\nMetro cities: 2-3 days\nOther cities: 4-5 days\nFree shipping above 999\nTrack via link in confirmation email', next: 'ask_more' },
        { id: 'ans_returns', type: 'message', text: 'Returns and Refunds\n\n7-day return policy\nRefund in 5-7 business days\nItems must be unused and in original packaging\nInitiate via website or WhatsApp', next: 'ask_more' },
        { id: 'ans_payment', type: 'message', text: 'Payment Methods\n\nUPI (GPay, PhonePe, Paytm)\nCredit/Debit cards\nNet Banking\nCash on Delivery above 500\nEMI available above 3000', next: 'ask_more' },
        {
          id: 'ask_more', type: 'question', text: 'Do you have another question? (yes / no)',
          variable: 'wants_more', fallback: 'Please reply yes or no.',
          options: [
            { match: 'yes', reply: null, next: 'faq_menu' },
            { match: 'no', reply: null, next: 'end' },
            { match: '*', reply: null, next: 'faq_menu' },
          ],
        },
        { id: 'escalate', type: 'escalate', message: 'Connecting you to our support team. They will respond within a few minutes!' },
        { id: 'end', type: 'end', text: 'Thanks for using our FAQ bot, {{contact_name}}! Feel free to message anytime. Have a great day!' },
      ],
    },
  },
  {
    name: 'Order Status Tracker',
    trigger_keyword: 'order',
    description: 'Let customers check order status by entering Order ID. Escalates to human for issues.',
    icon: '📦',
    flow_json: {
      steps: [
        { id: 'start', type: 'message', text: 'Hi {{contact_name}}! Welcome to Order Tracking.\n\nI can help you check your order status instantly.', next: 'ask_order_id' },
        { id: 'ask_order_id', type: 'question', text: 'Please enter your Order ID (e.g. ORD-12345):', variable: 'order_id', input_type: 'text', max_retries: 3, on_max_retries: 'escalate', next: 'show_status' },
        { id: 'show_status', type: 'message', text: 'Checking order {{order_id}}...\n\nOrder Found!\nStatus: Processing\nEstimated Delivery: 3-5 business days\n\nYou will get an SMS when it ships.', next: 'ask_issue' },
        {
          id: 'ask_issue', type: 'question',
          text: 'Is there an issue?\n\n1 No, just checking\n2 Item not received\n3 Damaged item\n4 Wrong item\n5 Want to cancel',
          variable: 'issue_type', fallback: 'Please reply 1 to 5.',
          options: [
            { match: '1', reply: 'Great! Let us know if you need anything else.', next: 'end' },
            { match: '2', reply: null, next: 'escalate' },
            { match: '3', reply: null, next: 'escalate' },
            { match: '4', reply: null, next: 'escalate' },
            { match: '5', reply: null, next: 'cancel' },
            { match: '*', reply: null, next: 'ask_issue' },
          ],
        },
        { id: 'cancel', type: 'message', text: 'Cancellation Request for Order {{order_id}}\n\nPossible only if order has not shipped. Our team will confirm within 2 hours.', next: 'end' },
        { id: 'escalate', type: 'escalate', message: 'Escalating your issue with Order {{order_id}} to our support team. Response within 1 hour!' },
        { id: 'end', type: 'end', text: 'Thank you {{contact_name}}! Order {{order_id}} noted. Have a great day!' },
      ],
    },
  },
];
